// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React from 'react';

/**
 * Reusable space station icon component - orbital facility design
 * @param {number} size - Width/height in pixels (default: 200)
 * @param {string} color - Primary color (default: #00ffff)
 * @param {boolean} animated - Enable rotation and blinking animations (default: true)
 * @param {object} style - Additional inline styles
 */
const StationIcon = ({ 
  size = 200, 
  color = '#00ffff',
  animated = true,
  style = {}
}) => {
  const fillColor = `rgba(${parseInt(color.slice(1, 3), 16)}, ${parseInt(color.slice(3, 5), 16)}, ${parseInt(color.slice(5, 7), 16)}, 0.2)`;
  const strokeColor = color;
  
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 200 200" 
      style={{ 
        filter: `drop-shadow(0 0 20px ${color})`,
        ...style
      }}
    >
      {/* Rotating outer ring */}
      <circle 
        cx="100" 
        cy="100" 
        r="85" 
        fill="none" 
        stroke={strokeColor} 
        strokeWidth="2" 
        strokeDasharray="10,5"
        opacity="0.6"
      >
        {animated && (
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 100 100"
            to="360 100 100"
            dur="30s"
            repeatCount="indefinite"
          />
        )}
      </circle>
      
      {/* Rotating middle ring */}
      <circle 
        cx="100" 
        cy="100" 
        r="60" 
        fill="none" 
        stroke={strokeColor} 
        strokeWidth="2.5" 
        strokeDasharray="5,3"
        opacity="0.7"
      >
        {animated && (
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="360 100 100"
            to="0 100 100"
            dur="20s"
            repeatCount="indefinite"
          />
        )}
      </circle>
      
      {/* Station core */}
      <circle 
        cx="100" 
        cy="100" 
        r="30" 
        fill={fillColor} 
        stroke={strokeColor} 
        strokeWidth="3"
      >
        {animated && (
          <animate 
            attributeName="opacity" 
            values="0.8;1;0.8" 
            dur="3s" 
            repeatCount="indefinite"
          />
        )}
      </circle>
      
      {/* Core center glow */}
      <circle 
        cx="100" 
        cy="100" 
        r="15" 
        fill={strokeColor} 
        opacity="0.6"
      >
        {animated && (
          <animate 
            attributeName="opacity" 
            values="0.4;0.8;0.4" 
            dur="2s" 
            repeatCount="indefinite"
          />
        )}
      </circle>
      
      {/* Left solar panel */}
      <rect 
        x="10" 
        y="75" 
        width="30" 
        height="50" 
        fill="rgba(0, 100, 200, 0.6)" 
        stroke={strokeColor} 
        strokeWidth="2"
        opacity="0.8"
      />
      <line x1="10" y1="85" x2="40" y2="85" stroke={strokeColor} strokeWidth="0.5" opacity="0.3"/>
      <line x1="10" y1="95" x2="40" y2="95" stroke={strokeColor} strokeWidth="0.5" opacity="0.3"/>
      <line x1="10" y1="105" x2="40" y2="105" stroke={strokeColor} strokeWidth="0.5" opacity="0.3"/>
      <line x1="10" y1="115" x2="40" y2="115" stroke={strokeColor} strokeWidth="0.5" opacity="0.3"/>
      
      {/* Right solar panel */}
      <rect 
        x="160" 
        y="75" 
        width="30" 
        height="50" 
        fill="rgba(0, 100, 200, 0.6)" 
        stroke={strokeColor} 
        strokeWidth="2"
        opacity="0.8"
      />
      <line x1="160" y1="85" x2="190" y2="85" stroke={strokeColor} strokeWidth="0.5" opacity="0.3"/>
      <line x1="160" y1="95" x2="190" y2="95" stroke={strokeColor} strokeWidth="0.5" opacity="0.3"/>
      <line x1="160" y1="105" x2="190" y2="105" stroke={strokeColor} strokeWidth="0.5" opacity="0.3"/>
      <line x1="160" y1="115" x2="190" y2="115" stroke={strokeColor} strokeWidth="0.5" opacity="0.3"/>
      
      {/* Antenna */}
      <line 
        x1="100" 
        y1="70" 
        x2="100" 
        y2="20" 
        stroke={strokeColor} 
        strokeWidth="2"
        opacity="0.8"
      />
      <circle 
        cx="100" 
        cy="20" 
        r="4" 
        fill={strokeColor}
      >
        {animated && (
          <animate 
            attributeName="opacity" 
            values="0.5;1;0.5" 
            dur="1.5s" 
            repeatCount="indefinite"
          />
        )}
      </circle>
      
      {/* Blinking lights around the station */}
      {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        const x = 100 + Math.cos(rad) * 45;
        const y = 100 + Math.sin(rad) * 45;
        return (
          <circle 
            key={i}
            cx={x} 
            cy={y} 
            r="2" 
            fill="#ffaa00"
          >
            {animated && (
              <animate 
                attributeName="opacity" 
                values="0.3;1;0.3" 
                dur="2s" 
                begin={`${i * 0.15}s`}
                repeatCount="indefinite"
              />
            )}
          </circle>
        );
      })}
    </svg>
  );
};

export default StationIcon;
